import * as React from 'react';
import { createContext, useContext, useMemo, useCallback } from "react";
import { useLocalStorage } from "./useLocalStorage";
import { API, Token } from '../io/Api';



interface AuthValue {
    getToken: () => Token | null;
    login: (username: string, password: string) => Promise<Token>;
    isActive: () => Promise<boolean>;
}
const AuthContext = createContext<AuthValue | null>(null);


interface Props {
    children: string | JSX.Element | JSX.Element[];
}

export const AuthProvider = ({ children }: Props): JSX.Element => {
    const [getToken, setToken] = useLocalStorage<Token | null>('token', null);

    const login = useCallback(async (username: string, password: string) => {
        const token = await API.login(username, password);
        if (token) {
            setToken(token);
            API.token = token.value;
            return token;
        }
        throw Error('No such user');
    }, [setToken]);

    const logout = useCallback(() => {
        setToken(null);
        API.token = ''
    }, [setToken]);

    const refreshTokens = useCallback(async () => {
        const token = getToken();
        if (token) {
            try {
                const newToken = await API.refreshToken(token.value);
                setToken(newToken);
                API.token = newToken.value;
                return true;
            }
            catch (error) {
                console.warn('Can`t refresh token');
                return false;
            }
        }
        console.warn('Can`t refresh token');
        return false;
    }, [getToken, setToken]);


    const isActive = useCallback(async () => {
        return (await refreshTokens()) || (await logout()) || false;
    }, [refreshTokens, logout]);

    const value = useMemo(() => {
        return { getToken, login, isActive };
    }, [getToken, login, isActive]
    );

    return <AuthContext.Provider value={value}>
        {children}
    </AuthContext.Provider>;
}

export const useAuth = (): AuthValue | null => {
    return useContext(AuthContext);
};

export const useToken = () => {
    const auth = useAuth();
    return auth?.getToken() || null;
}